// shop-frontend/src/pages/ShopMallProductDetail.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { apiGet, toAbsUrl, startAskBuyerConversation, getAskBuyers } from "../utils/api";
import { useShopMallCart } from "../context/ShopMallCartContext";
import { THEMES } from "../components/mall-theme/SectionComponents";
import ProductReviews from "../components/ProductReviews";
import "../styles/shopMallProductDetail.css";
import { 
  ArrowLeft, 
  ShoppingCart, 
  Plus, 
  Minus, 
  Star, 
  Package,
  Truck,
  Shield,
  Store,
  MessageCircle,
  Users
} from "lucide-react";

function safeNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function money(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
}

function pickImages(p) {
  const arr = [];
  if (p?.image) arr.push(p.image);
  if (p?.imageUrl) arr.push(p.imageUrl);
  if (p?.thumbnail) arr.push(p.thumbnail);
  if (p?.photo) arr.push(p.photo);
  if (Array.isArray(p?.images)) arr.push(...p.images);
  if (Array.isArray(p?.displayImages)) arr.push(...p.displayImages);
  if (p?.media?.image) arr.push(p.media.image);
  if (Array.isArray(p?.media?.images)) arr.push(...p.media.images);
  if (Array.isArray(p?.media) && p.media.length) {
    for (const m of p.media) if (m?.url) arr.push(m.url);
  }
  return Array.from(new Set(arr.filter(Boolean).map((url) => toAbsUrl(url)).filter(Boolean)));
}

export default function ShopMallProductDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Get theme from URL params or state or default to midnight
  const themeId = searchParams.get("theme") || location.state?.theme || "midnight";
  const theme = THEMES[themeId] || THEMES.midnight;

  const { addToCart, items: cartItems } = useShopMallCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  // Authoritative discount map built from shop's customSidebar — keyed by
  // "productId" and "productId__variantId" → pct
  const [shopDiscountMap, setShopDiscountMap] = useState({});

  // Discount from URL (when user landed via a discounted card link)
  const sidebarDiscountPct = Math.min(100, Math.max(0, Number(searchParams.get("discount") || 0)));
  const discountedVariantIds = searchParams.get("dvids")
    ? new Set(searchParams.get("dvids").split(","))
    : null;

  // Complete Ask Previous Buyers functionality (from user frontend)
  const [buyersOpen, setBuyersOpen] = useState(false);
  const [buyersLoading, setBuyersLoading] = useState(false);
  const [buyersErr, setBuyersErr] = useState("");
  const [buyers, setBuyers] = useState([]);

  // Get shop info from state (passed from mall navigation)
  const shopFromState = location.state?.shop;

  const images = useMemo(() => pickImages(product), [product]);

  // ── Variant helpers ────────────────────────────────────────────────────────
  const variants = useMemo(
    () => Array.isArray(product?.variants) ? product.variants.filter(v => v && v.isActive !== false) : [],
    [product]
  );
  const colorOptionName = useMemo(() => {
    for (const v of variants) for (const ov of (v.optionValues || []))
      if (ov.name && /^colou?r$/i.test(ov.name)) return ov.name;
    return '';
  }, [variants]);
  const sizeOptionName = useMemo(() => {
    for (const v of variants) for (const ov of (v.optionValues || []))
      if (ov.name && /^size$/i.test(ov.name)) return ov.name;
    return '';
  }, [variants]);
  const colorValues = useMemo(() => {
    if (!colorOptionName) return [];
    return [...new Set(variants.flatMap(v =>
      (v.optionValues || []).filter(ov => ov.name === colorOptionName).map(ov => ov.value)
    ).filter(Boolean))];
  }, [variants, colorOptionName]);
  const sizeValues = useMemo(() => {
    if (!sizeOptionName) return [];
    return [...new Set(variants.flatMap(v =>
      (v.optionValues || []).filter(ov => ov.name === sizeOptionName).map(ov => ov.value)
    ).filter(Boolean))];
  }, [variants, sizeOptionName]);
  const selectedVariant = useMemo(() => {
    if (!variants.length || !selectedColor) return null;
    return variants.find(v =>
      (!colorOptionName || (v.optionValues || []).find(ov => ov.name === colorOptionName)?.value === selectedColor) &&
      (!sizeOptionName || !selectedSize || (v.optionValues || []).find(ov => ov.name === sizeOptionName)?.value === selectedSize)
    ) || null;
  }, [variants, selectedColor, selectedSize, colorOptionName, sizeOptionName]);

  useEffect(() => {
    let cancelled = false;

    async function loadProduct() {
      setLoading(true);
      setError("");
      setProduct(null);
      
      // Reset buyers state when loading new product
      setBuyersOpen(false);
      setBuyers([]);
      setBuyersErr("");

      try {
        if (!productId) throw new Error("Missing product ID");

        // Try multiple endpoints to find the product
        const endpoints = [
          `/mall/products/${encodeURIComponent(productId)}`,
          `/mall/product/${encodeURIComponent(productId)}`,
          `/products/${encodeURIComponent(productId)}`,
          `/shop-products/${encodeURIComponent(productId)}`,
        ];

        let res = null;
        let lastErr = null;

        for (const ep of endpoints) {
          try {
            res = await apiGet(ep, { auth: false });
            break;
          } catch (e) {
            lastErr = e;
          }
        }

        const found = res?.product || res?.data?.product || res?.data || res;

        if (!found?._id && !found?.id) {
          throw new Error(lastErr?.message || "Product not found");
        }

        if (!cancelled) {
          setProduct(found);
          const imgs = pickImages(found);
          setActiveImg(imgs[0] || "");
          setQty(1);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e?.message || "Failed to load product");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadProduct();

    return () => { cancelled = true; };
  }, [productId]);

  // Pre-select variant from ?variantId= URL param or navigation state
  useEffect(() => {
    const urlVariantId = searchParams.get('variantId') || location.state?.variantId;
    if (!urlVariantId || !product || !variants.length) return;
    const v = variants.find(x => String(x._id) === String(urlVariantId));
    if (!v) return;
    const c = (v.optionValues || []).find(ov => ov.name === colorOptionName)?.value || '';
    const s = (v.optionValues || []).find(ov => ov.name === sizeOptionName)?.value || '';
    if (c) setSelectedColor(c);
    if (s) setSelectedSize(s);
    if (v.images?.[0]) setActiveImg(toAbsUrl(v.images[0]));
  }, [product, variants, colorOptionName, sizeOptionName, searchParams, location.state?.variantId]);

  // Resolve shop ID from product or location state, then fetch customSidebar
  const productShopId = product?.shopId || product?.shop?._id || product?.shop?.id || shopFromState?._id;
  useEffect(() => {
    if (!productShopId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet(`/public/shops/${encodeURIComponent(productShopId)}/mall`, { auth: false });
        const sidebar = res?.page?.customSidebar || res?.mallPage?.customSidebar || [];
        const map = {};
        const set = (k, pct) => { if (k && (!map[k] || map[k] < pct)) map[k] = pct; };
        for (const ind of sidebar) {
          for (const cat of (ind.categories || [])) {
            const pct = Number(cat.discount) || 0;
            if (pct <= 0) continue;
            for (const id of (cat.productIds || [])) set(id, pct);
          }
        }
        if (!cancelled) setShopDiscountMap(map);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [productShopId]);

  const title = product?.title || product?.name || "Product";
  const description = product?.description || product?.desc || "";
  const rawPrice = selectedVariant?.price ?? product?.localPrice ?? product?.price ?? 0;
  const selectedVid = String(selectedVariant?._id || "");
  const _pidForDiscount = String(product?._id || product?.id || productId || "");

  // Resolve discount for current variant — customSidebar is the sole source of truth
  const variantDiscountPct = (() => {
    let pct = 0;
    if (selectedVid && shopDiscountMap[`${_pidForDiscount}__${selectedVid}`]) {
      pct = Math.max(pct, shopDiscountMap[`${_pidForDiscount}__${selectedVid}`]);
    }
    if (shopDiscountMap[_pidForDiscount]) {
      pct = Math.max(pct, shopDiscountMap[_pidForDiscount]);
    }
    if (pct === 0 && sidebarDiscountPct > 0) {
      if (discountedVariantIds === null) pct = sidebarDiscountPct;
      else if (selectedVid && discountedVariantIds?.has(selectedVid)) pct = sidebarDiscountPct;
    }
    return Math.min(100, pct);
  })();

  const thisVariantDiscounted = variantDiscountPct > 0;
  const price = thisVariantDiscounted
    ? Math.round(rawPrice * (1 - variantDiscountPct / 100) * 100) / 100
    : rawPrice;
  const currency = String(product?.currency || "USD").toUpperCase();

  // Helpers for size-badge UI
  const isVariantDiscounted = (v) => {
    if (!v) return false;
    if (shopDiscountMap[_pidForDiscount]) return true;
    return shopDiscountMap[`${_pidForDiscount}__${String(v._id || "")}`] > 0;
  };
  const productHasAnyDiscount = useMemo(() => {
    if (shopDiscountMap[_pidForDiscount]) return true;
    return (variants || []).some(v =>
      shopDiscountMap[`${_pidForDiscount}__${String(v._id || "")}`] > 0);
  }, [shopDiscountMap, _pidForDiscount, variants]);

  // null = selection incomplete; number = exact variant/product stock
  const variantStock = selectedVariant != null
    ? safeNum(selectedVariant.stock, 99)
    : variants.length > 0
      ? null
      : safeNum(product?.quantity, 99);

  const _pid = String(product?._id || product?.id || "");
  const _vid = selectedVariant ? String(selectedVariant._id || "") : "";
  const activeCartId = _vid ? `${_pid}__${_vid}` : _pid;
  const alreadyInCart = (cartItems || []).find(
    (i) => (i.cartId || String(i.productId || i._id || "")) === activeCartId
  )?.qty || 0;

  const availableStock = variantStock !== null ? Math.max(0, variantStock - alreadyInCart) : null;
  const maxStock = availableStock ?? 0;
  const category = product?.categoryLabel || product?.category || "Category";
  const rating = safeNum(product?.rating, 0);
  const reviewCount = safeNum(product?.reviewCount, 0);

  // Get shop info (from product or from state)
  const shopId = product?.shopId || product?.shop?._id || product?.shop?.id || shopFromState?._id;
  const shopName = product?.shopName || product?.shop?.shopName || product?.shop?.name || shopFromState?.shopName || shopFromState?.name || "Shop";

  function dec() {
    setQty((q) => Math.max(1, Number(q) - 1));
  }

  function inc() {
    setQty((q) => Math.min(maxStock, Number(q) + 1));
  }

  function handleAddToCart() {
    if (!product) return;

    // Build a variant-unique cartId so Red and Blue t-shirts are separate entries
    const variantId = selectedVariant?._id || '';
    const cartId = variantId
      ? `${String(product._id || product.id)}__${variantId}`
      : String(product._id || product.id || '');

    // Build a readable variant label (Color / Size)
    const parts = [];
    if (selectedColor) parts.push(selectedColor);
    if (selectedSize) parts.push(selectedSize);
    const variantLabel = parts.join(' / ');

    // Pick the best image for this variant
    const variantImg =
      (Array.isArray(selectedVariant?.images) && selectedVariant.images.filter(Boolean)[0]) ||
      product.image || product.imageUrl || product.thumbnail || '';

    addToCart(
      {
        ...product,
        cartId,                                          // unique per variant
        _id: cartId,                                     // fallback key
        title: variantLabel ? `${title} — ${variantLabel}` : title,
        image: variantImg,
        price,                                           // already discount-adjusted
        originalPrice: thisVariantDiscounted ? rawPrice : undefined,
        discountPct: thisVariantDiscounted ? variantDiscountPct : undefined,
        shopName,
        variantLabel,
        variantId,
      },
      qty
    );

    alert(`✅ Added ${qty}× ${variantLabel ? `${title} — ${variantLabel}` : title} to cart!`);
  }

  // Complete Ask Previous Buyers functionality (from user frontend)
  const buyersRef = React.useRef(null);
  
  useEffect(() => {
    function handleClickOutside(e) {
      if (buyersRef.current && !buyersRef.current.contains(e.target)) {
        setBuyersOpen(false);
      }
    }
    if (buyersOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [buyersOpen]);

  function toggleBuyers() {
    if (buyersOpen) {
      setBuyersOpen(false);
    } else {
      loadBuyers();
    }
  }

  async function loadBuyers() {
    const pid = productId;
    if (!pid) return;

    setBuyersLoading(true);
    setBuyersErr("");
    setBuyers([]);
    setBuyersOpen(false);

    try {
      const list = await getAskBuyers(pid);
      setBuyers(list || []);
      setBuyersOpen(true);
    } catch (e) {
      setBuyersErr(e?.message || "Failed to load previous buyers");
      setBuyersOpen(true);
    } finally {
      setBuyersLoading(false);
    }
  }

  async function messageBuyer(buyer) {
    const userId = String(buyer?.userId || buyer?._id || "").trim();
    const anon = !!buyer?.anonymous;

    if (!userId || !productId) return;

    if (anon) {
      alert("This buyer is anonymous and cannot be messaged.");
      return;
    }

    try {
      const res = await startAskBuyerConversation({
        toUserId: userId,
        productId: productId,
      });

      const cid = String(
        res?.conversationId || res?._id || res?.conversation?._id || ""
      ).trim();

      if (!cid) throw new Error("Conversation not created");
      navigate(`/shop/messages/${encodeURIComponent(cid)}`);
    } catch (e) {
      alert(e?.message || "Failed to start chat with buyer");
    }
  }

  function goBack() {
    // Go back to mall preview
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate("/mall/preview");
    }
  }

  function goToShop() {
    if (shopId) {
      navigate(`/mall/preview?shop=${encodeURIComponent(shopId)}`);
    }
  }

  if (loading) {
    return (
      <div className="smpd-page-wrap" style={{ background: theme.bg, minHeight: "100vh", color: theme.text, textAlign: "center" }}>
        <p style={{ color: theme.muted }}>Loading product...</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="smpd-page-wrap" style={{ background: theme.bg, minHeight: "100vh", color: theme.text }}>
        <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
          <p style={{ color: theme.muted, marginBottom: 20 }}>{error || "Product not found"}</p>
          <button
            onClick={goBack}
            style={{
              background: theme.primary,
              color: "#fff",
              padding: "12px 24px",
              borderRadius: 8,
              border: "none",
              cursor: "pointer",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            ← Back to Mall
          </button>
        </div>
      </div>
    );
  }

  const qtySafe = Math.max(1, Math.floor(safeNum(qty, 1)));
  const total = safeNum(price, 0) * qtySafe;

  return (
    <div style={{ background: theme.bg, minHeight: "100vh", color: theme.text, fontFamily: theme.font }}>
      {/* Header */}
      <div style={{ 
        borderBottom: `1px solid ${theme.border}`,
        padding: "16px 20px",
        background: theme.card,
        position: "sticky",
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", gap: 16 }}>
          <button
            onClick={goBack}
            style={{
              background: "transparent",
              border: "none",
              color: theme.text,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            <ArrowLeft size={20} />
            Back
          </button>
          <div style={{ flex: 1 }} />
          {shopId && (
            <button
              onClick={goToShop}
              style={{
                background: "transparent",
                border: `1px solid ${theme.border}`,
                borderRadius: 8,
                padding: "8px 16px",
                color: theme.text,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
              }}
            >
              <Store size={16} />
              Visit Shop
            </button>
          )}
        </div>
      </div>

      {/* Product Content */}
      <div className="smpd-page-wrap">
        <div className="smpd-product-grid">
          {/* Images */}
          <div>
            {/* Main Image */}
            <div style={{
              background: theme.card,
              border: `1px solid ${theme.border}`,
              borderRadius: 12,
              overflow: "hidden",
              marginBottom: 16,
              aspectRatio: "1/1",
            }}>
              <img
                src={activeImg || images[0] || "/placeholder.png"}
                alt={title}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImg(img)}
                    style={{
                      width: 80,
                      height: 80,
                      background: theme.card,
                      border: `2px solid ${activeImg === img ? theme.primary : theme.border}`,
                      borderRadius: 8,
                      overflow: "hidden",
                      cursor: "pointer",
                      padding: 0,
                    }}
                  >
                    <img
                      src={img}
                      alt={`${title} ${idx + 1}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            {/* Category */}
            <p style={{ 
              color: theme.primary, 
              fontSize: 13, 
              fontWeight: 600, 
              marginBottom: 8,
              textTransform: "uppercase",
              letterSpacing: "0.05em"
            }}>
              {category}
            </p>

            {/* Title */}
            <h1 style={{ 
              fontSize: 32, 
              fontWeight: 700, 
              marginBottom: 12,
              lineHeight: 1.2,
            }}>
              {title}
            </h1>

            {/* Shop Name */}
            <div style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: 8, 
              marginBottom: 16,
              color: theme.muted,
              fontSize: 14,
            }}>
              <Store size={16} />
              <span>{shopName}</span>
            </div>

            {/* Rating */}
            {rating > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    fill={i < Math.floor(rating) ? theme.primary : "none"}
                    stroke={i < Math.floor(rating) ? theme.primary : theme.muted}
                  />
                ))}
                <span style={{ color: theme.muted, fontSize: 14 }}>
                  {rating.toFixed(1)} ({reviewCount} reviews)
                </span>
              </div>
            )}

            {/* Price */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                <p style={{
                  fontSize: 36,
                  fontWeight: 700,
                  color: thisVariantDiscounted ? "#ef4444" : theme.primary,
                  marginBottom: 4,
                }}>
                  {currency} {money(price)}
                </p>
                {thisVariantDiscounted && (
                  <>
                    <span style={{ fontSize: 20, color: theme.muted, textDecoration: "line-through" }}>
                      {currency} {money(rawPrice)}
                    </span>
                    <span style={{
                      fontSize: 13, fontWeight: 700, color: "#fff",
                      background: "#f97316", padding: "3px 8px", borderRadius: 8,
                    }}>
                      {variantDiscountPct}% OFF
                    </span>
                  </>
                )}
              </div>
              {productHasAnyDiscount && !thisVariantDiscounted && (
                <p style={{ color: "#f97316", fontSize: 13, fontWeight: 600, marginTop: 2 }}>
                  Discount available on selected sizes — pick a highlighted size to save
                </p>
              )}
              <p style={{ color: theme.muted, fontSize: 14, marginTop: 4 }}>
                Total: {currency} {money(total)}
              </p>
            </div>

            {/* Colour Selector */}
            {colorValues.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 10, color: theme.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Colour{selectedColor ? `: ${selectedColor}` : ''}
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {colorValues.map(color => {
                    const isActive = selectedColor.toLowerCase() === color.toLowerCase();
                    const SWATCH = { red:'#ef4444', blue:'#3b82f6', green:'#22c55e', yellow:'#eab308', black:'#111827', white:'#f9fafb', pink:'#ec4899', purple:'#a855f7', orange:'#f97316', brown:'#92400e', grey:'#6b7280', gray:'#6b7280', navy:'#1e3a8a', beige:'#d4c8a8', teal:'#14b8a6' };
                    const swatch = SWATCH[color.toLowerCase()];
                    const rows = variants.filter(v => (v.optionValues || []).find(ov => ov.name === colorOptionName)?.value?.toLowerCase() === color.toLowerCase());
                    const enabled = rows.some(v => v.isActive !== false && Number(v.stock || 0) > 0);
                    return (
                      <button key={color} disabled={!enabled} title={color}
                        onClick={() => {
                          setSelectedColor(color);
                          const best = rows.find(v => v.isActive !== false && Number(v.stock || 0) > 0) || rows[0];
                          const raw = best?.images?.[0] || images[0] || "";
                          if (raw) setActiveImg(toAbsUrl(typeof raw === 'string' ? raw : (raw?.url || raw?.secure_url || raw?.path || '')));
                        }}
                        style={{ width: 36, height: 36, borderRadius: '50%', background: swatch || '#ccc', border: isActive ? `3px solid ${theme.primary}` : `2px solid ${theme.border}`, boxShadow: isActive ? `0 0 0 2px ${theme.bg}, 0 0 0 4px ${theme.primary}` : 'none', cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.4, padding: 0, flexShrink: 0 }}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Size Selector */}
            {sizeValues.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 10, color: theme.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Size{selectedSize ? `: ${selectedSize}` : ''}
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {sizeValues.map(size => {
                    const isActive = selectedSize.toLowerCase() === size.toLowerCase();
                    const relevantVariants = variants.filter(v =>
                      (!colorOptionName || !selectedColor || (v.optionValues || []).find(ov => ov.name === colorOptionName)?.value?.toLowerCase() === selectedColor.toLowerCase()) &&
                      (v.optionValues || []).find(ov => ov.name === sizeOptionName)?.value?.toLowerCase() === size.toLowerCase()
                    );
                    const enabled = relevantVariants.some(v => v.isActive !== false && Number(v.stock || 0) > 0);
                    // Pick the discounted variant for THIS size (within selected colour)
                    const sizeVariant = relevantVariants.find(v => isVariantDiscounted(v)) || relevantVariants[0];
                    const sizeDiscounted = isVariantDiscounted(sizeVariant);
                    const sizePct = sizeVariant
                      ? Math.max(
                          shopDiscountMap[`${_pidForDiscount}__${String(sizeVariant._id || "")}`] || 0,
                          shopDiscountMap[_pidForDiscount] || 0,
                          sidebarDiscountPct || 0
                        )
                      : 0;
                    return (
                      <button key={size} disabled={!enabled} onClick={() => setSelectedSize(size)}
                        style={{ position: 'relative', padding: '8px 16px', borderRadius: 8, background: isActive ? theme.primary : theme.card, color: isActive ? '#fff' : theme.text, border: isActive ? `2px solid ${theme.primary}` : (sizeDiscounted ? '2px solid #f97316' : `2px solid ${theme.border}`), fontWeight: isActive ? 700 : 400, fontSize: 14, cursor: enabled ? 'pointer' : 'not-allowed', opacity: enabled ? 1 : 0.4 }}
                      >
                        {size}
                        {sizeDiscounted && (
                          <span style={{
                            position: 'absolute', top: -8, right: -8,
                            fontSize: 9, fontWeight: 800, color: '#fff',
                            background: '#f97316', padding: '1px 4px', borderRadius: 6,
                            lineHeight: 1.3,
                          }}>
                            -{sizePct}%
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity Selector */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ 
                display: "block", 
                fontSize: 14, 
                fontWeight: 600, 
                marginBottom: 8,
                color: theme.muted,
              }}>
                Quantity
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <button
                  onClick={dec}
                  disabled={qty <= 1}
                  style={{
                    width: 40,
                    height: 40,
                    background: theme.card,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 8,
                    color: theme.text,
                    cursor: qty <= 1 ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: qty <= 1 ? 0.5 : 1,
                  }}
                >
                  <Minus size={18} />
                </button>
                <span style={{ 
                  fontSize: 18, 
                  fontWeight: 600,
                  minWidth: 40,
                  textAlign: "center",
                }}>
                  {qty}
                </span>
                <button
                  onClick={inc}
                  disabled={qty >= maxStock}
                  style={{
                    width: 40,
                    height: 40,
                    background: theme.card,
                    border: `1px solid ${theme.border}`,
                    borderRadius: 8,
                    color: theme.text,
                    cursor: qty >= maxStock ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    opacity: qty >= maxStock ? 0.5 : 1,
                  }}
                >
                  <Plus size={18} />
                </button>
                <span style={{ color: theme.muted, fontSize: 13 }}>
                  {availableStock === null
                    ? "Select options"
                    : availableStock === 0
                      ? (alreadyInCart > 0 ? `${alreadyInCart} in cart` : "Out of stock")
                      : `${availableStock} available${alreadyInCart > 0 ? ` (${alreadyInCart} in cart)` : ""}`
                  }
                </span>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              style={{
                width: "100%",
                background: theme.primary,
                color: "#fff",
                padding: "16px 24px",
                borderRadius: 12,
                border: "none",
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                marginBottom: 16,
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => e.target.style.background = theme.primaryHover}
              onMouseLeave={(e) => e.target.style.background = theme.primary}
            >
              <ShoppingCart size={20} />
              Add to Mall Cart
            </button>

            {/* Features */}
            <div style={{ 
              background: theme.card,
              border: `1px solid ${theme.border}`,
              borderRadius: 12,
              padding: 20,
              marginTop: 24,
            }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Package size={20} style={{ color: theme.primary }} />
                  <span style={{ fontSize: 14, color: theme.muted }}>In Stock</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Truck size={20} style={{ color: theme.primary }} />
                  <span style={{ fontSize: 14, color: theme.muted }}>Fast Shipping</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Shield size={20} style={{ color: theme.primary }} />
                  <span style={{ fontSize: 14, color: theme.muted }}>Buyer Protection</span>
                </div>
              </div>
            </div>

            {/* Description */}
            {description && (
              <div style={{ marginTop: 32 }}>
                <h3 style={{ 
                  fontSize: 18, 
                  fontWeight: 700, 
                  marginBottom: 12,
                }}>
                  Description
                </h3>
                <p style={{ 
                  color: theme.muted, 
                  fontSize: 14, 
                  lineHeight: 1.6,
                  whiteSpace: "pre-wrap",
                }}>
                  {description}
                </p>
              </div>
            )}

            {/* Ask Previous Buyers Section (Complete implementation) */}
            <div style={{
              background: theme.card,
              border: `1px solid ${theme.border}`,
              borderRadius: 12,
              padding: 20,
              marginTop: 24,
            }} ref={buyersRef}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                <div>
                  <h3 style={{ 
                    fontSize: 16,
                    fontWeight: 700,
                    marginBottom: 4,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}>
                    <Users size={20} style={{ color: theme.primary }} />
                    Ask Previous Buyers
                  </h3>
                  <p style={{ 
                    color: theme.muted, 
                    fontSize: 14,
                    margin: 0,
                  }}>
                    Connect directly with people who bought this product
                  </p>
                </div>
                <button
                  onClick={toggleBuyers}
                  disabled={buyersLoading || !productId}
                  style={{
                    background: buyersOpen ? theme.accent + '40' : theme.primary,
                    color: buyersOpen ? theme.text : "#fff",
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: `1px solid ${theme.border}`,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: buyersLoading || !productId ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    opacity: buyersLoading || !productId ? 0.6 : 1,
                  }}
                >
                  <Users size={16} />
                  {buyersLoading ? "Loading buyers..." : buyersOpen ? "Close buyers ▲" : "Ask previous buyers ▼"}
                </button>
              </div>

              {/* Buyers List */}
              {buyersOpen && (
                <div style={{
                  background: theme.bg,
                  border: `1px solid ${theme.border}`,
                  borderRadius: 8,
                  padding: 16,
                  marginTop: 12,
                }}>
                  {buyersErr ? (
                    <div style={{ color: "#ff8080", fontWeight: 600, textAlign: "center" }}>
                      {buyersErr}
                    </div>
                  ) : buyers.length === 0 ? (
                    <div style={{ 
                      color: theme.muted, 
                      textAlign: "center", 
                      padding: 20,
                      fontSize: 14,
                    }}>
                      No buyers opted-in yet. After completed orders + consent, they will appear here.
                    </div>
                  ) : (
                    <div style={{ display: "grid", gap: 12 }}>
                      {buyers.map((b, idx) => {
                        const userId = String(b.userId || "").trim();
                        const anon = !!b.anonymous;
                        
                        // Build display name from backend response
                        const firstName = String(b.firstName || "").trim();
                        const lastName = String(b.lastName || "").trim();
                        const username = String(b.username || "").trim();
                        
                        const displayName = anon 
                          ? "Anonymous buyer"
                          : firstName && lastName 
                          ? `${firstName} ${lastName}`
                          : firstName 
                          ? firstName
                          : username
                          ? username
                          : "Buyer";
                          
                        const avatar = anon ? "" : String(b.avatar || "").trim();

                        return (
                          <div
                            key={userId || `buyer-${idx}`}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 12,
                              padding: "12px 0",
                              borderBottom: idx < buyers.length - 1 ? `1px solid ${theme.border}` : "none",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              {avatar ? (
                                <img
                                  src={toAbsUrl(avatar)}
                                  alt=""
                                  style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: "50%",
                                    objectFit: "cover",
                                    border: `1px solid ${theme.border}`,
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: "50%",
                                    background: theme.accent + '20',
                                    border: `1px solid ${theme.border}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: theme.primary,
                                  }}
                                >
                                  {displayName.charAt(0).toUpperCase()}
                                </div>
                              )}
                              <div style={{ fontWeight: 600, fontSize: 14 }}>{displayName}</div>
                            </div>

                            <button
                              onClick={() => messageBuyer(b)}
                              disabled={!userId || anon}
                              title={anon ? "Anonymous buyer cannot be messaged" : "Message buyer"}
                              style={{
                                background: !userId || anon ? theme.muted + '20' : theme.primary,
                                color: !userId || anon ? theme.muted : "#fff",
                                padding: "6px 12px",
                                borderRadius: 6,
                                border: "none",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: !userId || anon ? "not-allowed" : "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                opacity: !userId || anon ? 0.5 : 1,
                              }}
                            >
                              <MessageCircle size={14} />
                              Message
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Product Reviews Section */}
            <div style={{ marginTop: 32 }}>
              <ProductReviews 
                productId={product?._id || product?.id} 
                defaultOpen={false}
                theme={theme}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
